// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import PropTypes from 'prop-types'
import { useState } from 'react'
import { FormField, RadioButtonGroup } from 'grommet'
import { useTranslation } from 'react-i18next'

import { WORKSPACE_SCOPES } from '../constants'
import { isMSP } from '../../../../utils/feature-flag-utils'

import ScopeGroupMultiSelect from './ScopeGroupMultiSelect'

const WorkspaceScopeSelection = ({
  handleSelection,
  scopeOption = '',
  preselectScopeGroups = [],
  scopeError,
  setApiError,
  isCCSRole = false,
  hasMSPOneRoleAppScope,
  setHasConflictingMSPOneRoleAppRA = () => {},
  setMSPScopeSelected = () => {}
}) => {
  const { t } = useTranslation(['common', 'iam'])
  const [showScopeGroupDropdown, setShowScopeGroupDropdown] = useState(
    scopeOption === WORKSPACE_SCOPES?.SPECIFIC_SCOPE_GROUPS
  )
  const scopeOptions = [
    ...(isMSP()
      ? [
          {
            label: t('iam:msp.workspace_scope_none'),
            value: WORKSPACE_SCOPES?.NONE
          },
          {
            label: t('iam:msp.msp_workspace_scope_entire', {
              workspace: t('common:business_object.wkspc')
            }),
            value: WORKSPACE_SCOPES?.ENTIRE
          }
        ]
      : [
          {
            label: t('iam:assign_roles_side_panel.access_all_workspace_label', {
              workspace: t('common:business_object.wkspc')
            }),
            value: WORKSPACE_SCOPES?.ENTIRE
          }
        ]),
    ...(!isCCSRole
      ? [
          {
            label: t('iam:msp.msp_workspace_scope_specific'),
            value: WORKSPACE_SCOPES?.SPECIFIC_SCOPE_GROUPS
          }
        ]
      : [])
  ]

  return (
    <>
      <FormField
        required
        label={
          isMSP()
            ? t('iam:msp.msp_workspace_scope', {
                workspace: t('common:business_object.wkspc')
              })
            : t('authz:scope_groups.tab_labels.scope')
        }
        error={scopeError?.scopeError}
        data-testid="scope-radio-btn-form"
      >
        <RadioButtonGroup
          name="msp-workspace-scope"
          options={scopeOptions}
          labelKey="label"
          valueKey="value"
          value={scopeOption}
          onChange={(event) => {
            scopeError?.clearError()
            handleSelection(event?.value, 'scope')
            if (isMSP()) {
              if (
                event?.value === WORKSPACE_SCOPES?.ENTIRE ||
                event?.value === WORKSPACE_SCOPES?.SPECIFIC_SCOPE_GROUPS
              ) {
                setMSPScopeSelected(true)
                setShowScopeGroupDropdown(
                  event?.value === WORKSPACE_SCOPES?.SPECIFIC_SCOPE_GROUPS
                )
                setHasConflictingMSPOneRoleAppRA(hasMSPOneRoleAppScope)
              } else {
                // Else condition for when scope selection is NONE
                setHasConflictingMSPOneRoleAppRA(false)
                setMSPScopeSelected(false)
                setShowScopeGroupDropdown(false)
              }
            } else {
              setShowScopeGroupDropdown(
                event?.value === WORKSPACE_SCOPES?.SPECIFIC_SCOPE_GROUPS
              )
            }
          }}
        />
      </FormField>
      {showScopeGroupDropdown && (
        <ScopeGroupMultiSelect
          setSelectedScopeGroups={(scopeGroups) => {
            handleSelection(scopeGroups, 'scopeGroupInstances')
          }}
          preselectScopeGroups={preselectScopeGroups}
          scopeError={scopeError}
          setApiError={setApiError}
        />
      )}
    </>
  )
}

WorkspaceScopeSelection.propTypes = {
  handleSelection: PropTypes.func.isRequired,
  scopeOption: PropTypes.string.isRequired,
  preselectScopeGroups: PropTypes.array,
  scopeError: PropTypes.object.isRequired,
  isCCSRole: PropTypes.bool.isRequired,
  setApiError: PropTypes.func.isRequired,
  hasMSPOneRoleAppScope: PropTypes.bool,
  setHasConflictingMSPOneRoleAppRA: PropTypes.func,
  setMSPScopeSelected: PropTypes.func
}

export default WorkspaceScopeSelection
